import {Token, QuestsApi, QuestProgression} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {useQuery, keepPreviousData} from "@tanstack/react-query";

async function fetchQuestProgressionForUser(fullToken: Token, questConfigIds: Array<string>, challengeSlug?: string, challengeId?: string): Promise<Array<QuestProgression>> {
    return await new QuestsApi(getConfig(fullToken)).getQuestProgression({
        questConfigIds: questConfigIds,
        challengeSlug: challengeSlug,
        challengeId: challengeId,
    });
}

function getQuestProgressionForUserQuery(fullToken: Token, questConfigIds: Array<string>, challengeSlug?: string, challengeId?: string) {
    const enabled = Boolean(fullToken && (questConfigIds?.length > 0 || challengeId || challengeSlug));

    const sortedQuestConfigIds = questConfigIds && Array.isArray(questConfigIds) ? [...questConfigIds].sort() : [];

    return {
        queryKey: [
            "quest-progression-user",
            fullToken?.id ?? "unauthenticated",
            sortedQuestConfigIds,
            challengeSlug,
            challengeId,
        ],
        queryFn: () => fetchQuestProgressionForUser(fullToken, sortedQuestConfigIds, challengeSlug, challengeId),
        /*
         * v5: `keepPreviousData: true` (v4) is silently ignored — the option was
         * replaced by `placeholderData: keepPreviousData`. Without this, a query-
         * key change (e.g. the play-page quest set changes mid-session) drops
         * `data` to undefined until refetch, which flash-reflashes any UI gating
         * on "progressions loaded" (the onboarding rail's `ready` flag).
         */
        placeholderData: keepPreviousData,
        staleTime: 2 * 60 * 1000, // 2 minutes - fallback when WebSocket fails
        refetchOnWindowFocus: "always",
        refetchOnMount: false, // Don't auto-refetch on mount - rely on WebSocket updates
        enabled: enabled,
    } as const;
}

export function useGetQuestProgressionForUser(fullToken: Token, questConfigIds: Array<string>, challengeSlug?: string, challengeId?: string) {
    return useQuery(getQuestProgressionForUserQuery(fullToken, questConfigIds, challengeSlug, challengeId));
}